import { Tag, Tooltip } from 'antd';

import {
	describeBatches,
	type ProcessKind,
	type ProcessRequirement,
} from '../../interfaces/ProcessCatalog.Interface';

/** Colour per kind, so the three read apart at a glance on a busy page. */
const KIND_COLORS: Record<ProcessKind, string> = {
	finishing: 'orange',
	handling: 'blue',
	cleaning: 'cyan',
};

interface Props {
	kind: ProcessKind;
	requirement: ProcessRequirement | null | undefined;
	/** Batch counts are useful beside the finish, noise in a dense table. */
	showBatches?: boolean;
	/**
	 * What the process was resolved *from*, when that wording is worth keeping.
	 * The finish is matched by token, so a drawing asking for "Bead Blast 120
	 * Grit" lands on a row named "Bead Blasting"; the tag stands in for the
	 * text, so the tooltip is where the original survives.
	 */
	note?: string;
}

/**
 * The process a job needs, as a tag standing in for the text it replaces.
 *
 * Carries no kind prefix: it is always shown under a heading that already says
 * which one it is. Renders nothing when the process is standard, which is most
 * jobs — callers can drop it in unconditionally.
 */
export default function SpecialProcessTag({
	kind,
	requirement,
	showBatches = false,
	note,
}: Props) {
	if (!requirement || !requirement.is_special) return null;

	const batches = showBatches ? describeBatches(requirement) : '';
	const source = note && note !== requirement.name ? `from "${note}"` : '';
	const tooltip = [requirement.instructions, batches, source]
		.filter(Boolean)
		.join(' — ');

	const tag = (
		<Tag color={KIND_COLORS[kind]} style={{ margin: 0, fontWeight: 600 }}>
			{requirement.name}
		</Tag>
	);

	return tooltip ? <Tooltip title={tooltip}>{tag}</Tooltip> : tag;
}
